import React, { useState, useEffect, useMemo } from 'react';
import KeyHandover from './Keyhandover';
import { Link, useNavigate } from 'react-router-dom';
import AssignRoomModal from './RoomAssignmentModal';
import { toast } from 'react-toastify';
import DamageReportModal from './DamageReportModal';
import CheckoutConfirmationModal from './CheckoutConfirmationModal';
import InvoiceModal from './InvoiceModal';
import { FiX, FiCheckCircle, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { AiOutlineWarning } from 'react-icons/ai';
import { FaHammer } from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Assignment reasons — must match backend ALLOWED_ASSIGNMENT_REASONS exactly
// (bq/backend/app/api/checkIn/services/room_assignment.py)
const ASSIGNMENT_REASONS = [
  { value: '', label: '-- Select reason --' },
  { value: 'complimentary_upgrade', label: 'Complimentary Upgrade (rooms full)' },
  { value: 'price_match', label: 'Price Match (same price, better room)' },
  { value: 'paid_upgrade', label: 'Paid Upgrade (guest paid extra)' },
  { value: 'downgrade', label: 'Downgrade (assigned lower room)' },
  { value: 'substitution', label: 'Substitution (similar room type)' },
  { value: 'vip', label: 'VIP Guest Upgrade' },
  { value: 'maintenance', label: 'Room Maintenance Issue' },
  { value: 'guest_request', label: 'Guest Requested Change' },
];

const ReservationTable = ({ lastNameQuery, bookingIdQuery, dateFilter, dateRange }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allReservations, setAllReservations] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDamageModal, setShowDamageModal] = useState(false);
  const [showAssignRoomModal, setShowAssignRoomModal] = useState(false);
  const [showReassignRoomModal, setShowReassignRoomModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [newRoomNumber, setNewRoomNumber] = useState('');
  const [reassignAvailableRooms, setReassignAvailableRooms] = useState([]);
  const [reassignReason, setReassignReason] = useState('');
  const [reassignNotes, setReassignNotes] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [cancellationResponse, setCancellationResponse] = useState(null);

  // ========== EDIT RESERVATION STATES ==========
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRoomTypes, setEditRoomTypes] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [editFormData, setEditFormData] = useState({
    number_of_guests: 1,
    special_requests: '',
  });

  const reservationsPerPage = 40;
  const navigate = useNavigate();

  const fetchReservations = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/bookings/`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      const data = await response.json();
      if (data && data.bookings) {
        setAllReservations(data.bookings);
        setReservations(data.bookings);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  useEffect(() => {
    if (dateFilter === 'all' || !dateRange || !dateRange.start || !dateRange.end) {
      setReservations(allReservations);
      setCurrentPage(1);
      return;
    }

    const startDate = dateRange.start.toISOString().split('T')[0];
    const endDate = dateRange.end.toISOString().split('T')[0];

    const filtered = allReservations.filter((reservation) => {
      const checkinDate = reservation.checkindate;
      if (!checkinDate) return false;
      return checkinDate >= startDate && checkinDate <= endDate;
    });

    setReservations(filtered);
    setCurrentPage(1);
  }, [dateFilter, dateRange, allReservations]);

  const filteredReservations = useMemo(() => {
    return reservations.filter((reservation) => {
      const lastNameMatches = reservation.lastname
        ? reservation.lastname.toLowerCase().includes(lastNameQuery.toLowerCase())
        : true;
      const bookingIdMatches = reservation.bookingid
        ? reservation.bookingid.toLowerCase().includes(bookingIdQuery.toLowerCase())
        : true;

      return lastNameMatches && bookingIdMatches;
    });
  }, [reservations, lastNameQuery, bookingIdQuery]);

  // Reset to page 1 when search queries change
  useEffect(() => {
    setCurrentPage(1);
  }, [lastNameQuery, bookingIdQuery]);

  // Pagination calculations - MEMOIZED
  const { currentReservations, totalPages, indexOfFirstReservation } = useMemo(() => {
    const indexOfLast = currentPage * reservationsPerPage;
    const indexOfFirst = indexOfLast - reservationsPerPage;
    const current = filteredReservations.slice(indexOfFirst, indexOfLast);
    const total = Math.max(1, Math.ceil(filteredReservations.length / reservationsPerPage));

    return {
      currentReservations: current,
      totalPages: total,
      indexOfFirstReservation: indexOfFirst,
    };
  }, [filteredReservations, currentPage, reservationsPerPage]);

  // Clamp currentPage if it exceeds totalPages (e.g., after filtering)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // FIXED: Better visible pages logic
  const getVisiblePages = () => {
    const pages = [];
    const pageGroupSize = 5;
    const currentGroup = Math.ceil(currentPage / pageGroupSize);
    const startPage = (currentGroup - 1) * pageGroupSize + 1;
    const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const addSecurityDeposit = async (orderId, roomTypeName) => {
    try {
      const roomTypesResponse = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      const roomTypes = await roomTypesResponse.json();
      const matchingRoomType = roomTypes.find(
        (type) => type.roomtypename.toLowerCase() === roomTypeName.toLowerCase()
      );
      if (!matchingRoomType) return;
      const depositAmount = parseInt(matchingRoomType.deposit_amount);
      if (!depositAmount || depositAmount <= 0) return;

      const depositResponse = await fetch(`${BQ_BASE_URL}/bq/api/add-security-deposit/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_id: orderId, amount: depositAmount }),
      });
      const depositResult = await depositResponse.json();
      if (depositResult.success) {
        console.log('Security deposit added successfully:', depositResult);
      }
    } catch (error) {
      console.error('Error adding security deposit:', error);
    }
  };

  const handleKeyHandover = (bookingId) => {
    const reservation = reservations.find((r) => r.bookingid === bookingId);
    setSelectedReservation(reservation);
    setShowModal(true);
  };

  const handleAssignRoom = (bookingId) => {
    const reservation = reservations.find((r) => r.bookingid === bookingId);
    setSelectedReservation(reservation);
    setShowAssignRoomModal(true);
  };

  const handleReassignRoom = async (bookingId) => {
    const reservation = reservations.find((r) => r.bookingid === bookingId);
    setSelectedReservation(reservation);
    setNewRoomNumber('');
    setReassignReason('');
    setReassignNotes('');
    setShowReassignRoomModal(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/availability`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      const data = await response.json();
      setReassignAvailableRooms(data?.available_rooms || []);
    } catch (error) {
      console.error('Error fetching available rooms:', error);
      setReassignAvailableRooms([]);
    }
  };

  // ========== EDIT RESERVATION HANDLERS ==========
  const handleOpenEditModal = async (bookingId) => {
    const reservation = reservations.find((r) => r.bookingid === bookingId);
    if (!reservation) return;

    setSelectedReservation(reservation);
    setEditErrors({});
    setEditFormData({
      number_of_guests: reservation.numberofguests || 1,
      special_requests: reservation.special_requests || '',
    });
    setShowEditModal(true);

    // Fetch room types if not loaded
    if (editRoomTypes.length === 0) {
      setEditLoading(true);
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
          method: 'GET',
          headers: { accept: 'application/json' },
        });
        const data = await response.json();
        if (data && Array.isArray(data)) setEditRoomTypes(data);
      } catch (error) {
        console.error('Error fetching room types:', error);
      } finally {
        setEditLoading(false);
      }
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditErrors((prev) => ({ ...prev, [name]: '' }));
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    const newErrors = {};

    const bookedRoomTypeId = selectedReservation?.booked_room_type?.id;
    const bookedRoomType = editRoomTypes.find((r) => r.roomtypeid === bookedRoomTypeId);
    if (bookedRoomType && editFormData.number_of_guests > bookedRoomType.max_occupancy) {
      newErrors.number_of_guests = `Max ${bookedRoomType.max_occupancy} guests allowed`;
    }

    if (Object.keys(newErrors).length > 0) {
      setEditErrors(newErrors);
      return;
    }

    setEditSaving(true);
    try {
      const payload = {
        bookingid: selectedReservation.bookingid,
        number_of_guests: parseInt(editFormData.number_of_guests),
        special_requests: editFormData.special_requests || '',
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/update-reservation/`, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to update reservation');
      }

      // Update local state
      const updatedFields = {
        numberofguests: parseInt(editFormData.number_of_guests),
        special_requests: editFormData.special_requests,
      };

      setReservations((prev) =>
        prev.map((r) =>
          r.bookingid === selectedReservation.bookingid ? { ...r, ...updatedFields } : r
        )
      );
      setAllReservations((prev) =>
        prev.map((r) =>
          r.bookingid === selectedReservation.bookingid ? { ...r, ...updatedFields } : r
        )
      );

      toast.success('Reservation updated successfully!');
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating reservation:', error);
      toast.error(error.message || 'Failed to update reservation');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditFormData({
      number_of_guests: 1,
      special_requests: '',
    });
    setEditErrors({});
  };

  // ========== END EDIT HANDLERS ==========

  const handleCloseModal = () => {
    setShowModal(false);
    setShowAssignRoomModal(false);
    setShowReassignRoomModal(false);
    setShowCancelModal(false);
    setShowInvoiceModal(false);
    setCancellationReason('');
    setCancellationResponse(null);
    setCancellationLoading(false);
    setNewRoomNumber('');
    setReassignReason('');
    setReassignNotes('');
  };

  const handleHandoverKey = async (bookingId) => {
    const updatedReservations = reservations.map((reservation) =>
      reservation.bookingid === bookingId ? { ...reservation, keyhandover: 'Handed' } : reservation
    );
    setReservations(updatedReservations);
    setAllReservations((prev) =>
      prev.map((r) => (r.bookingid === bookingId ? { ...r, keyhandover: 'Handed' } : r))
    );
    setShowModal(false);

    const reservation = reservations.find((r) => r.bookingid === bookingId);
    if (reservation && reservation.orderid && reservation.roomtypename) {
      await addSecurityDeposit(reservation.orderid, reservation.roomtypename);
    }
  };

  const handleAssignRoomSubmit = async (
    bookingId,
    roomNumber,
    assignmentReason,
    assignmentNotes
  ) => {
    try {
      const params = new URLSearchParams({
        booking_id: bookingId,
        room_number: roomNumber,
      });
      if (assignmentReason) params.set('assignment_reason', assignmentReason);
      if (assignmentNotes) params.set('assignment_notes', assignmentNotes);

      const response = await fetch(`${BQ_BASE_URL}/bq/api/assign-room/?${params.toString()}`, {
        method: 'POST',
        headers: { accept: 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || data.message || 'Failed to assign room.');
        return;
      }
      if (data.message === 'Room assigned successfully') {
        await fetchReservations();
        setShowAssignRoomModal(false);
        toast.success('Room assigned successfully!');
      }
    } catch (error) {
      toast.error('Failed to assign room.');
    }
  };

  const reassignSelectedRoom = reassignAvailableRooms.find(
    (r) => String(r.roomnumber) === String(newRoomNumber)
  );
  const reassignBookedRoomTypeName =
    selectedReservation?.booked_room_type?.roomtypename || selectedReservation?.roomtypename;
  const reassignRoomTypeChanged =
    reassignSelectedRoom && reassignSelectedRoom.room_type !== reassignBookedRoomTypeName;

  const handleReassignRoomSubmit = async () => {
    if (!newRoomNumber.trim()) {
      toast.error('Please select a room number');
      return;
    }
    if (reassignRoomTypeChanged && !reassignReason) {
      toast.error('Please select a reason — the selected room is a different room type');
      return;
    }
    if (reassignRoomTypeChanged && !reassignNotes.trim()) {
      toast.error('Please add notes — the selected room is a different room type');
      return;
    }

    setReassignLoading(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/reassign-room/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingid: selectedReservation.bookingid,
          new_room_number: parseInt(newRoomNumber),
          assignment_reason: reassignRoomTypeChanged ? reassignReason : null,
          assignment_notes: reassignRoomTypeChanged ? reassignNotes.trim() : null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        await fetchReservations();
        toast.success(`Room reassigned to ${data.new_room_number} successfully!`);
        setShowReassignRoomModal(false);
        setNewRoomNumber('');
        setReassignReason('');
        setReassignNotes('');
      } else {
        toast.error(data.detail || data.message || 'Failed to reassign room');
      }
    } catch (error) {
      console.error('Error reassigning room:', error);
      toast.error('An error occurred while reassigning room');
    } finally {
      setReassignLoading(false);
    }
  };

  const handleRowClick = (guestId) => {
    navigate(`/dashboard/guest/${guestId}`);
  };

  const handleCancelBooking = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    setCancellationLoading(true);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/modify/${selectedReservation.bookingid}/cancel`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ cancellation_reason: cancellationReason }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong while cancelling the booking.');
      }

      setCancellationResponse(data);

      if (data.status === 'success') {
        const updatedReservations = reservations.map((reservation) =>
          reservation.bookingid === selectedReservation.bookingid
            ? { ...reservation, booking_status: 'Cancelled' }
            : reservation
        );
        setReservations(updatedReservations);
        setAllReservations((prev) =>
          prev.map((r) =>
            r.bookingid === selectedReservation.bookingid
              ? { ...r, booking_status: 'Cancelled' }
              : r
          )
        );
        toast.success('Booking cancelled successfully!');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.message || 'An unexpected error occurred.');
      handleCloseModal();
    } finally {
      setCancellationLoading(false);
    }
  };

  const resetCancellationFlow = () => {
    setCancellationReason('');
    setCancellationResponse(null);
    setCancellationLoading(false);
    setShowCancelModal(false);
  };

  const formatToTwoDecimal = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const handleOpenModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowDamageModal(true);
  };

  const handleCheckout = async (reservation) => {
    try {
      const checkoutResponse = await fetch(
        `${BQ_BASE_URL}/bq/api/check-out?orderid=${reservation.orderid}`,
        {
          method: 'POST',
          headers: { accept: 'application/json' },
        }
      );

      const checkoutData = await checkoutResponse.json();

      if (!checkoutResponse.ok) {
        if (checkoutData.detail) {
          toast.error(
            typeof checkoutData.detail === 'string'
              ? checkoutData.detail
              : checkoutData.detail?.message || 'Checkout failed'
          );
        } else {
          toast.error('Checkout failed');
        }
        return;
      }

      if (checkoutData.success) {
        toast.success(checkoutData.message || 'Checkout successful!');

        const clearResponse = await fetch(`${BQ_BASE_URL}/bq/api/clear-checkout-status/`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ booking_id: reservation.bookingid }),
        });

        const clearData = await clearResponse.json();

        if (clearData.success) {
          toast.success(clearData.message || 'Checkout status cleared successfully');
          const updatedReservations = reservations.map((r) =>
            r.bookingid === reservation.bookingid
              ? { ...r, is_checkout_cleared: true, booking_status: 'Checkedout' }
              : r
          );
          setReservations(updatedReservations);
          setAllReservations((prev) =>
            prev.map((r) =>
              r.bookingid === reservation.bookingid
                ? { ...r, is_checkout_cleared: true, booking_status: 'Checkedout' }
                : r
            )
          );
        } else {
          toast.warning('Checkout completed but status update failed');
        }

        setShowCheckoutModal(false);
      } else {
        toast.error(checkoutData.message || 'Checkout failed');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error('An error occurred during checkout');
    }
  };

  const handleSendInvoice = async (orderId) => {
    try {
      setInvoiceLoading(true);
      setInvoiceLoadingId(orderId);

      const reservation = reservations.find((r) => r.orderid === orderId);
      if (!reservation) {
        toast.error('Reservation not found');
        return;
      }

      const recipientEmail = reservation.emailid;
      if (!recipientEmail) {
        toast.error('Guest email not found');
        return;
      }

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/invoice/email/${orderId}?recipient_email=${encodeURIComponent(recipientEmail)}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send invoice email');

      toast.success(
        <div>
          <strong>Invoice Sent!</strong>
          <br />
          Invoice has been sent to {recipientEmail}
        </div>,
        { position: 'top-center', autoClose: 5000 }
      );
      setShowInvoiceModal(false);
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice. Please try again.');
    } finally {
      setInvoiceLoading(false);
      setInvoiceLoadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading reservations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-4">
      {dateFilter !== 'all' && dateRange && dateRange.start && dateRange.end && (
        <div className="mb-3 px-4 py-2 bg-gray-200 border border-gray-200 rounded-lg text-sm text-gray-800">
          <span className="font-medium">Showing check-in dates from:</span>{' '}
          {dateRange.start.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
          {' to '}
          {dateRange.end.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
          <span className="ml-2 text-xs bg-blue-100 px-2 py-1 rounded-full">
            {filteredReservations.length} reservations found
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse border border-gray-300 text-[15px]">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-300 px-1 py-2 text-center w-12">Sr. No.</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-28">Reservation No.</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-28">Booking Id.</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-36">Guest Name</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-32">Room Type</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-20">Room No.</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-28">Check-In</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-28">Check-Out</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-20">Type</th>
              <th className="border border-gray-300 py-2 text-center w-16">Total Guests</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-20">Guest</th>
              <th className="border border-gray-300 px-1 py-2 text-center w-56">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentReservations.length > 0 ? (
              currentReservations.map((reservation, index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-100 transition cursor-pointer"
                  onClick={() => handleRowClick(reservation.guestid)}
                >
                  <td className="border border-gray-300 px-1 py-2 text-center">
                    {index + 1 + indexOfFirstReservation}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">{reservation.orderid}</td>
                  <td className="border border-gray-300 px-2 py-2">{reservation.bookingid}</td>
                  <td className="border border-gray-300 px-2 py-2 capitalize">
                    {reservation.firstname} {reservation.lastname}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">
                    {reservation.assigned_room_type &&
                    reservation.assigned_room_type.id !== reservation.booked_room_type?.id ? (
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 line-through">
                          {reservation.booked_room_type?.roomtypename || reservation.roomtypename}
                        </span>
                        <span className="font-semibold text-blue-700">
                          {reservation.assigned_room_type.roomtypename}
                        </span>
                      </div>
                    ) : (
                      reservation.booked_room_type?.roomtypename || reservation.roomtypename
                    )}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">
                    {reservation.roomnumber ? reservation.roomnumber : 'N/A'}
                  </td>
                  <td className="border border-gray-300 px-1 py-2 whitespace-nowrap">
                    {reservation.checkindate}
                  </td>
                  <td className="border border-gray-300 px-1 py-2 whitespace-nowrap">
                    {reservation.checkoutdate}
                  </td>
                  <td className="border border-gray-300 px-1 py-2 text-center">
                    {reservation.booking_type}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">{reservation.numberofguests}</td>
                  <td className="border border-gray-300 px-4 py-2">
                    {reservation.clienttype === 'Leisure' || reservation.clienttype === 'leisure'
                      ? 'Leisure'
                      : reservation.clienttype === 'Corporate' ||
                          reservation.clienttype === 'corporate'
                        ? 'Corporate'
                        : reservation.clienttype || 'L'}
                  </td>
                  <td className="border border-gray-300 px-1 py-1 text-center">
                    {reservation.booking_status === 'Cancelled' ? (
                      <span className="text-gray-500 text-sm font-semibold">Cancelled</span>
                    ) : reservation.booking_status === 'Checkedout' ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReservation(reservation);
                            handleSendInvoice(reservation.orderid);
                          }}
                          disabled={invoiceLoading && invoiceLoadingId === reservation.orderid}
                          className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors relative group"
                          title="Send Invoice"
                        >
                          {invoiceLoading && invoiceLoadingId === reservation.orderid ? (
                            <svg
                              className="animate-spin h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          ) : (
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          )}
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Send Invoice
                          </span>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {reservation.roomid && reservation.roomnumber ? (
                          reservation.booking_status === 'Hard' ||
                          reservation.keyhandover === 'Handed' ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReassignRoom(reservation.bookingid);
                              }}
                              className="p-1.5 rounded-lg bg-gray-900 hover:bg-gray-950 text-white transition-colors relative group"
                              title="Reassign Room"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              </svg>
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                Reassign Room
                              </span>
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleKeyHandover(reservation.bookingid);
                                }}
                                className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group"
                                title="Key Handover"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                                  />
                                </svg>
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                  Key Handover
                                </span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReassignRoom(reservation.bookingid);
                                }}
                                className="p-1.5 rounded-lg bg-gray-900 hover:bg-gray-950 text-white transition-colors relative group"
                                title="Reassign Room"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                  Reassign Room
                                </span>
                              </button>
                            </>
                          )
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssignRoom(reservation.bookingid);
                            }}
                            className="p-1.5 rounded-lg bg-gray-900 hover:bg-gray-950 text-white transition-colors relative group"
                            title="Assign Room"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                              />
                            </svg>
                            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              Assign Room
                            </span>
                          </button>
                        )}

                        {/* NEW: Edit Reservation button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(reservation.bookingid);
                          }}
                          className="p-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors relative group"
                          title="Edit Reservation"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Edit Reservation
                          </span>
                        </button>

                        {/* Cancel */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReservation(reservation);
                            setCancellationResponse(null);
                            setCancellationReason('');
                            setShowCancelModal(true);
                          }}
                          className="p-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors relative group"
                          title="Cancel Booking"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Cancel Booking
                          </span>
                        </button>

                        {/* Upgrade */}
                        <Link
                          to="/dashboard/core-modules/property-management-system/front-desk-operations/room-upgrade"
                          onClick={(e) => {
                            e.stopPropagation();
                            sessionStorage.setItem(
                              'selectedReservation',
                              JSON.stringify(reservation)
                            );
                          }}
                          className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group inline-flex"
                          title="Upgrade Room"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                            />
                          </svg>
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Upgrade Room
                          </span>
                        </Link>

                        {/* Damage */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModal(reservation);
                          }}
                          className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group"
                          title="Report Damage"
                        >
                          <FaHammer className="h-4 w-4" />
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Report Damage
                          </span>
                        </button>

                        {/* Checkout / Invoice */}
                        {reservation.booking_status === 'Checkedout' ||
                        reservation.is_checkout_cleared ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedReservation(reservation);
                              handleSendInvoice(reservation.orderid);
                            }}
                            disabled={invoiceLoading && invoiceLoadingId === reservation.orderid}
                            className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors relative group"
                            title="Send Invoice"
                          >
                            {invoiceLoading && invoiceLoadingId === reservation.orderid ? (
                              <svg
                                className="animate-spin h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            ) : (
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            )}
                            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              Send Invoice
                            </span>
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedReservation(reservation);
                              setShowCheckoutModal(true);
                            }}
                            className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group"
                            title="Checkout"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              />
                            </svg>
                            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              Checkout
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center py-4 text-gray-500">
                  {dateFilter !== 'all'
                    ? `No reservations found for the selected date range.`
                    : 'No reservations found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredReservations.length > 0 && (
        <div className="flex justify-center mt-8 text-xs gap-2 items-center flex-wrap">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
          >
            Previous
          </button>

          {/* Show first page + ellipsis if current group is beyond first */}
          {currentPage > 10 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className={`px-2 py-2 mx-1 min-w-[35px] ${
                  currentPage === 1
                    ? 'bg-black text-white rounded-full'
                    : 'bg-gray-300 text-black rounded-full hover:bg-gray-400'
                }`}
              >
                1
              </button>
              {currentPage > 11 && <span className="px-2">...</span>}
            </>
          )}

          {getVisiblePages().map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-2 py-2 mx-1 min-w-[35px] ${
                currentPage === page
                  ? 'bg-black text-white rounded-full'
                  : 'bg-gray-300 text-black rounded-full hover:bg-gray-400'
              }`}
            >
              {page}
            </button>
          ))}

          {/* Show last page + ellipsis if not in current group */}
          {currentPage < totalPages - 9 && (
            <>
              {currentPage < totalPages - 10 && <span className="px-2">...</span>}
              <button
                onClick={() => handlePageChange(totalPages)}
                className={`px-2 py-2 mx-1 min-w-[35px] ${
                  currentPage === totalPages
                    ? 'bg-black text-white rounded-full'
                    : 'bg-gray-300 text-black rounded-full hover:bg-gray-400'
                }`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
          >
            Next
          </button>
        </div>
      )}

      {/* ========== EDIT RESERVATION MODAL ========== */}
      {showEditModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl relative overflow-hidden shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <button
              onClick={handleCloseEditModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-2 rounded-full transition-colors z-10 hover:bg-gray-100"
            >
              <FiX className="h-5 w-5" />
            </button>

            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <svg
                    className="h-6 w-6 text-blue-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Edit Reservation</h2>
                  <p className="text-sm text-gray-500">
                    Booking ID: {selectedReservation.bookingid} | Reservation:{' '}
                    {selectedReservation.orderid}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {/* Guest & Stay Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Guest</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {selectedReservation.firstname} {selectedReservation.lastname}
                  </p>
                  <p className="text-xs text-gray-500">{selectedReservation.emailid}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Stay</p>
                  <p className="font-medium text-gray-900">
                    {selectedReservation.checkindate} → {selectedReservation.checkoutdate}
                  </p>
                </div>
              </div>

              {/* Booked Room Type (READ ONLY) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booked Room Type{' '}
                  <span className="text-xs text-gray-400">
                    (guest's original booking — read-only)
                  </span>
                </label>
                <input
                  type="text"
                  value={
                    selectedReservation.booked_room_type?.roomtypename ||
                    selectedReservation.roomtypename ||
                    ''
                  }
                  readOnly
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is what the guest booked and will be charged for.
                </p>
              </div>

              {/* Assigned Room Type / Actual Room (READ ONLY — set only via Assign/Reassign Room) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Room Type{' '}
                  <span className="text-xs text-gray-400">(actual room given)</span>
                </label>
                <input
                  type="text"
                  value={selectedReservation.assigned_room_type?.roomtypename || 'Not yet assigned'}
                  readOnly
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use "Assign Room" / "Reassign Room" to change the physical room — the assigned
                  room type is derived from that, not editable here.
                </p>
              </div>

              {/* Show assignment summary only when a room has actually been assigned to a different type */}
              {selectedReservation.assigned_room_type && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <FiInfo className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800 space-y-1">
                    <p className="font-semibold">Room Assignment</p>
                    <p className="text-xs">
                      Actual Room:{' '}
                      <strong>{selectedReservation.actual_room?.roomnumber || '—'}</strong>
                    </p>
                    {selectedReservation.assignment_reason && (
                      <p className="text-xs">
                        Reason:{' '}
                        <strong>
                          {ASSIGNMENT_REASONS.find(
                            (r) => r.value === selectedReservation.assignment_reason
                          )?.label || selectedReservation.assignment_reason}
                        </strong>
                      </p>
                    )}
                    {selectedReservation.assignment_notes && (
                      <p className="text-xs">Notes: {selectedReservation.assignment_notes}</p>
                    )}
                    {selectedReservation.revenue_forgone > 0 && (
                      <p className="text-xs text-orange-700">
                        Revenue Forgone:{' '}
                        <strong>₹{Number(selectedReservation.revenue_forgone).toFixed(2)}</strong>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Factors (READ ONLY) */}
              {(selectedReservation.factor_total > 0 ||
                (selectedReservation.selected_factors || []).length > 0) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Applied Factors
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    Factor Total: ₹{Number(selectedReservation.factor_total || 0).toFixed(2)}
                  </p>
                  {(selectedReservation.selected_factors || []).length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selected Factor IDs: {(selectedReservation.selected_factors || []).join(', ')}
                    </p>
                  )}
                </div>
              )}

              {/* Number of Guests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Guests *
                </label>
                <input
                  type="number"
                  name="number_of_guests"
                  value={editFormData.number_of_guests}
                  onChange={handleEditInputChange}
                  min="1"
                  max={
                    editRoomTypes.find(
                      (r) => r.roomtypeid === selectedReservation.booked_room_type?.id
                    )?.max_occupancy || 10
                  }
                  className={`w-full px-3 py-2 border rounded-lg ${
                    editErrors.number_of_guests ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editErrors.number_of_guests && (
                  <p className="text-red-500 text-xs mt-1">{editErrors.number_of_guests}</p>
                )}
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Requests
                </label>
                <textarea
                  name="special_requests"
                  value={editFormData.special_requests}
                  onChange={handleEditInputChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                  placeholder="Any special requests..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseEditModal}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={editSaving}
                className={`px-6 py-2 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${
                  editSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:bg-gray-800'
                }`}
              >
                {editSaving ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ========== END EDIT MODAL ========== */}

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-screen-md max-h-[90vh] overflow-y-auto rounded-lg">
            <KeyHandover
              reservation={selectedReservation}
              onClose={handleCloseModal}
              onHandoverKey={handleHandoverKey}
            />
          </div>
        </div>
      )}

      {showAssignRoomModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-screen-md max-h-[90vh] overflow-y-auto rounded-lg">
            <AssignRoomModal
              reservation={selectedReservation}
              onClose={handleCloseModal}
              onAssignRoom={handleAssignRoomSubmit}
            />
          </div>
        </div>
      )}

      {showReassignRoomModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl relative overflow-hidden shadow-2xl w-full max-w-md">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-2 rounded-full transition-colors z-10"
            >
              <FiX className="h-5 w-5" />
            </button>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FiRefreshCw className="h-6 w-6 text-blue-600" />
                Reassign Room
              </h3>
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Current Room:{' '}
                  <span className="font-semibold text-gray-900">
                    {selectedReservation.roomnumber}
                  </span>{' '}
                  (Booked: {reassignBookedRoomTypeName || '—'})
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Room *</label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  value={newRoomNumber}
                  onChange={(e) => setNewRoomNumber(e.target.value)}
                  autoFocus
                >
                  <option value="">-- Select an available room --</option>
                  {reassignAvailableRooms.map((room) => (
                    <option key={room.roomid} value={room.roomnumber}>
                      {room.roomnumber} — {room.room_type}
                    </option>
                  ))}
                </select>

                {reassignRoomTypeChanged && (
                  <div className="mt-4 space-y-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                      Selected room is <strong>{reassignSelectedRoom.room_type}</strong>, different
                      from the booked <strong>{reassignBookedRoomTypeName}</strong>. A reason and
                      notes are required.
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Room Change *
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        value={reassignReason}
                        onChange={(e) => setReassignReason(e.target.value)}
                      >
                        {ASSIGNMENT_REASONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes *
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows="2"
                        value={reassignNotes}
                        onChange={(e) => setReassignNotes(e.target.value)}
                        placeholder="Explain the room change"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassignRoomSubmit}
                  disabled={
                    !newRoomNumber.trim() ||
                    reassignLoading ||
                    (reassignRoomTypeChanged && (!reassignReason || !reassignNotes.trim()))
                  }
                  className={`px-6 py-3 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${
                    !newRoomNumber.trim() ||
                    reassignLoading ||
                    (reassignRoomTypeChanged && (!reassignReason || !reassignNotes.trim()))
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gray-900 hover:bg-gray-950'
                  }`}
                >
                  {reassignLoading ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Reassigning...
                    </>
                  ) : (
                    <>
                      <FiRefreshCw className="h-4 w-4" />
                      Reassign Room
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl relative overflow-hidden shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <button
              onClick={resetCancellationFlow}
              className="absolute top-4 right-4 text-red-500 hover:text-red-700 p-2 rounded-full transition-colors z-10 bg-white/80"
            >
              <FiX className="h-5 w-5" />
            </button>
            <div className="overflow-y-auto flex-1">
              {cancellationResponse ? (
                <div className="p-6">
                  <div className="text-center mb-8">
                    <div className="mx-auto mb-4">
                      {cancellationResponse.status === 'success' ? (
                        <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                          <FiCheckCircle className="h-10 w-10 text-green-600" />
                        </div>
                      ) : (
                        <AiOutlineWarning className="mx-auto text-red-600 text-6xl mb-4" />
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {cancellationResponse.status === 'success'
                        ? 'Reservation Cancelled Successfully'
                        : 'Cancellation Failed'}
                    </h3>
                    <p className="text-gray-600 mb-6">{cancellationResponse.message}</p>
                    <div className="inline-flex flex-col items-center px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
                      <p className="text-xs text-gray-500 font-medium mb-1">RESERVATION NUMBER</p>
                      <p className="text-lg font-mono font-bold text-gray-900">
                        {selectedReservation.orderid}
                      </p>
                    </div>
                  </div>

                  {cancellationResponse.status === 'success' && cancellationResponse.data && (
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                          Financial Summary
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-gray-700">Cancellation Charges</span>
                            </div>
                            <span className="font-semibold text-red-600">
                              ₹{formatToTwoDecimal(cancellationResponse.data.room_penalty)}
                            </span>
                          </div>
                          <div className="">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-gray-700">Additional Service Refunds</span>
                              <span className="font-semibold text-gray-950">
                                ₹{formatToTwoDecimal(cancellationResponse.data.service_refund)}
                              </span>
                            </div>
                            {cancellationResponse.data.non_refundable_service_retained > 0 && (
                              <div className="flex justify-between items-center mt-1 text-sm">
                                <span className="text-gray-600">
                                  Non-refundable services retained
                                </span>
                                <span className="text-red-500">
                                  ₹
                                  {formatToTwoDecimal(
                                    cancellationResponse.data.non_refundable_service_retained
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2 pt-4 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700 font-medium">Total Refund</span>
                              <span className="text-xl font-bold text-green-600">
                                ₹{formatToTwoDecimal(cancellationResponse.data.total_refund)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {cancellationResponse.data.cancelled_service_items?.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                            Cancelled Services (
                            {cancellationResponse.data.cancelled_service_items.length})
                          </h4>
                          <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                            {cancellationResponse.data.cancelled_service_items.map(
                              (item, index) => (
                                <div
                                  key={index}
                                  className={`p-4 rounded-lg border ${
                                    item.is_refundable
                                      ? 'bg-green-50 border-green-100'
                                      : 'bg-gray-50 border-gray-100'
                                  }`}
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <span className="font-medium text-gray-900">
                                        {item.name || item.title}
                                      </span>
                                      <div className="flex items-center mt-1">
                                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 mr-2">
                                          {item.type === 'room_service'
                                            ? 'Room Service'
                                            : item.type === 'food'
                                              ? 'Food'
                                              : item.type === 'amenity'
                                                ? 'Amenity'
                                                : item.type}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          Qty: {item.quantity_cancelled || item.quantity}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <span className="font-medium">
                                        ₹{formatToTwoDecimal(item.total_amount)}
                                      </span>
                                      <div
                                        className={`text-xs mt-1 px-2 py-1 rounded-full ${
                                          item.is_refundable
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}
                                      >
                                        {item.is_refundable ? 'Refundable' : 'Non-refundable'}
                                      </div>
                                    </div>
                                  </div>
                                  {item.refundable_amount > 0 && item.is_refundable && (
                                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                                      <span className="text-sm text-gray-600">
                                        Refundable amount
                                      </span>
                                      <span className="text-sm font-medium text-green-600">
                                        ₹{formatToTwoDecimal(item.refundable_amount)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      <div
                        className={`p-4 rounded-lg border ${
                          cancellationResponse.data.total_refund > 0
                            ? 'bg-green-50 border-green-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-bold text-gray-900 text-lg">
                            {cancellationResponse.data.total_refund > 0
                              ? 'Final Refund Amount'
                              : 'No Refund Applicable'}
                          </span>
                          <span
                            className={`text-2xl font-bold ${
                              cancellationResponse.data.total_refund > 0
                                ? 'text-green-600'
                                : 'text-gray-600'
                            }`}
                          >
                            ₹{formatToTwoDecimal(cancellationResponse.data.total_refund)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 flex items-start">
                          <FiInfo className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                          {cancellationResponse.data.total_refund > 0
                            ? 'This amount will be refunded to your original payment method within 5-7 business days. All pending services have been cancelled.'
                            : 'No refund applicable as per cancellation policy. All pending services have been cancelled.'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full">
                  <div className="px-6 py-6 bg-gradient-to-r from-gray-50 to-gray-50 border-b border-gray-200">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Cancel Reservation</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Reservation Number: {selectedReservation.orderid}
                        </p>
                      </div>
                      <div className="bg-gray-100 px-3 py-1 rounded-full">
                        <span className="text-xs font-medium text-gray-700">
                          {selectedReservation.firstname} {selectedReservation.lastname}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <AiOutlineWarning className="h-5 w-5 text-yellow-600 mr-2" />
                        Cancellation Rules
                      </h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start">
                          <span className="text-yellow-600 mr-2">•</span>
                          Cancellation charges may apply as per the hotel policy
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 mr-2">•</span>
                          All pending services (amenities, food orders, room services) will be
                          automatically cancelled
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 mr-2">•</span>
                          Refundable amounts will be processed within 5-7 business days
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 mr-2">•</span>
                          Non-refundable services will be retained and charged
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 mr-2">•</span>A detailed financial
                          breakdown will be shown after cancellation
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Reason for Cancellation *
                        </label>
                        <textarea
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all placeholder:text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Please share your reason for cancelling this reservation..."
                          value={cancellationReason}
                          onChange={(e) => setCancellationReason(e.target.value)}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Your feedback helps us improve our services.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!cancellationResponse ? (
              <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={resetCancellationFlow}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={handleCancelBooking}
                  disabled={!cancellationReason.trim() || cancellationLoading}
                  className={`px-8 py-3 rounded-lg font-medium text-white transition-colors ${
                    !cancellationReason.trim() || cancellationLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 shadow-lg'
                  }`}
                >
                  {cancellationLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Confirm Cancellation'
                  )}
                </button>
              </div>
            ) : (
              <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end">
                <button
                  onClick={resetCancellationFlow}
                  className="px-8 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors shadow-lg"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showDamageModal && selectedReservation && (
        <DamageReportModal
          isOpen={showDamageModal}
          onClose={() => setShowDamageModal(false)}
          reservation={selectedReservation}
        />
      )}

      {showCheckoutModal && selectedReservation && (
        <CheckoutConfirmationModal
          reservation={selectedReservation}
          onClose={() => setShowCheckoutModal(false)}
          onConfirm={() => handleCheckout(selectedReservation)}
        />
      )}

      {showInvoiceModal && selectedReservation && (
        <InvoiceModal reservation={selectedReservation} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default ReservationTable;

// import React, { useState, useEffect } from 'react';
// import KeyHandover from './Keyhandover';
// import { Link, useNavigate } from 'react-router-dom';
// import AssignRoomModal from './RoomAssignmentModal';
// import { toast } from 'react-toastify';
// import DamageReportModal from './DamageReportModal';
// import CheckoutConfirmationModal from './CheckoutConfirmationModal';
// import InvoiceModal from './InvoiceModal';
// import { FiX, FiCheckCircle, FiInfo, FiRefreshCw } from 'react-icons/fi';
// import { AiOutlineWarning } from 'react-icons/ai';
// import { FaHammer } from 'react-icons/fa';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const ReservationTable = ({ lastNameQuery, bookingIdQuery, dateFilter, dateRange }) => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [allReservations, setAllReservations] = useState([]);
//   const [reservations, setReservations] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [showModal, setShowModal] = useState(false);
//   const [showDamageModal, setShowDamageModal] = useState(false);
//   const [showAssignRoomModal, setShowAssignRoomModal] = useState(false);
//   const [showReassignRoomModal, setShowReassignRoomModal] = useState(false);
//   const [showCancelModal, setShowCancelModal] = useState(false);
//   const [selectedReservation, setSelectedReservation] = useState(null);
//   const [showCheckoutModal, setShowCheckoutModal] = useState(false);
//   const [showInvoiceModal, setShowInvoiceModal] = useState(false);
//   const [reassignLoading, setReassignLoading] = useState(false);
//   const [newRoomNumber, setNewRoomNumber] = useState('');
//   const [invoiceLoading, setInvoiceLoading] = useState(false);
//   const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
//   const [cancellationReason, setCancellationReason] = useState('');
//   const [cancellationLoading, setCancellationLoading] = useState(false);
//   const [cancellationResponse, setCancellationResponse] = useState(null);

//   const reservationsPerPage = 15;
//   const navigate = useNavigate();

//   // Fetch all reservations once
//   useEffect(() => {
//     const fetchReservations = async () => {
//       setIsLoading(true);
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/bookings/`, {
//           method: 'GET',
//           headers: {
//             accept: 'application/json',
//           },
//         });
//         const data = await response.json();
//         if (data && data.bookings) {
//           setAllReservations(data.bookings);
//           setReservations(data.bookings);
//         }
//       } catch (error) {
//         console.error('Error fetching reservations:', error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchReservations();
//   }, []);

//   // Apply date filter based on checkindate
//   useEffect(() => {
//     if (dateFilter === 'all' || !dateRange || !dateRange.start || !dateRange.end) {
//       setReservations(allReservations);
//       setCurrentPage(1);
//       return;
//     }

//     const startDate = dateRange.start.toISOString().split('T')[0];
//     const endDate = dateRange.end.toISOString().split('T')[0];

//     const filtered = allReservations.filter((reservation) => {
//       const checkinDate = reservation.checkindate;
//       if (!checkinDate) return false;
//       return checkinDate >= startDate && checkinDate <= endDate;
//     });

//     setReservations(filtered);
//     setCurrentPage(1);
//   }, [dateFilter, dateRange, allReservations]);

//   // Filter by search queries (last name and booking ID)
//   const filteredReservations = reservations.filter((reservation) => {
//     const lastNameMatches = reservation.lastname
//       ? reservation.lastname.toLowerCase().includes(lastNameQuery.toLowerCase())
//       : true;
//     const bookingIdMatches = reservation.bookingid
//       ? reservation.bookingid.toLowerCase().includes(bookingIdQuery.toLowerCase())
//       : true;

//     return lastNameMatches && bookingIdMatches;
//   });

//   const indexOfLastReservation = currentPage * reservationsPerPage;
//   const indexOfFirstReservation = indexOfLastReservation - reservationsPerPage;
//   const currentReservations = filteredReservations.slice(
//     indexOfFirstReservation,
//     indexOfLastReservation
//   );

//   const totalPages = Math.ceil(filteredReservations.length / reservationsPerPage);

//   const handlePageChange = (pageNumber) => {
//     setCurrentPage(pageNumber);
//   };

//   const handleNextPage = () => {
//     if (currentPage < totalPages) {
//       setCurrentPage(currentPage + 1);
//     }
//   };

//   const handlePrevPage = () => {
//     if (currentPage > 1) {
//       setCurrentPage(currentPage - 1);
//     }
//   };

//   const getVisiblePages = () => {
//     const pages = [];
//     const pageGroupSize = 10;
//     const currentGroup = Math.ceil(currentPage / pageGroupSize);
//     const startPage = (currentGroup - 1) * pageGroupSize + 1;
//     const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

//     for (let i = startPage; i <= endPage; i++) {
//       pages.push(i);
//     }
//     return pages;
//   };

//   // ... rest of your functions (addSecurityDeposit, handleKeyHandover, etc.) remain the same ...

//   const addSecurityDeposit = async (orderId, roomTypeName) => {
//     try {
//       const roomTypesResponse = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       const roomTypes = await roomTypesResponse.json();

//       const matchingRoomType = roomTypes.find(
//         (type) => type.roomtypename.toLowerCase() === roomTypeName.toLowerCase()
//       );

//       if (!matchingRoomType) {
//         console.error('Room type not found:', roomTypeName);
//         return;
//       }

//       const depositAmount = parseInt(matchingRoomType.deposit_amount);

//       if (!depositAmount || depositAmount <= 0) {
//         console.log('No deposit amount configured for this room type');
//         return;
//       }

//       const depositResponse = await fetch(`${BQ_BASE_URL}/bq/api/add-security-deposit/`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({
//           order_id: orderId,
//           amount: depositAmount,
//         }),
//       });

//       const depositResult = await depositResponse.json();

//       if (depositResult.success) {
//         console.log('Security deposit added successfully:', depositResult);
//       } else {
//         console.error('Failed to add security deposit:', depositResult);
//       }
//     } catch (error) {
//       console.error('Error adding security deposit:', error);
//     }
//   };

//   const handleKeyHandover = (bookingId) => {
//     const reservation = reservations.find((r) => r.bookingid === bookingId);
//     setSelectedReservation(reservation);
//     setShowModal(true);
//   };

//   const handleAssignRoom = (bookingId) => {
//     const reservation = reservations.find((r) => r.bookingid === bookingId);
//     setSelectedReservation(reservation);
//     setShowAssignRoomModal(true);
//   };

//   const handleReassignRoom = (bookingId) => {
//     const reservation = reservations.find((r) => r.bookingid === bookingId);
//     setSelectedReservation(reservation);
//     setNewRoomNumber('');
//     setShowReassignRoomModal(true);
//   };

//   const handleCloseModal = () => {
//     setShowModal(false);
//     setShowAssignRoomModal(false);
//     setShowReassignRoomModal(false);
//     setShowCancelModal(false);
//     setShowInvoiceModal(false);
//     setCancellationReason('');
//     setCancellationResponse(null);
//     setCancellationLoading(false);
//     setNewRoomNumber('');
//   };

//   const handleHandoverKey = async (bookingId) => {
//     const updatedReservations = reservations.map((reservation) =>
//       reservation.bookingid === bookingId ? { ...reservation, keyhandover: 'Handed' } : reservation
//     );
//     setReservations(updatedReservations);
//     setAllReservations((prev) =>
//       prev.map((r) => (r.bookingid === bookingId ? { ...r, keyhandover: 'Handed' } : r))
//     );
//     setShowModal(false);

//     const reservation = reservations.find((r) => r.bookingid === bookingId);
//     if (reservation && reservation.orderid && reservation.roomtypename) {
//       await addSecurityDeposit(reservation.orderid, reservation.roomtypename);
//     }
//   };

//   const handleAssignRoomSubmit = async (bookingId, roomNumber) => {
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/assign-room/?booking_id=${bookingId}&room_number=${roomNumber}`,
//         {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//           },
//         }
//       );
//       const data = await response.json();
//       if (data.message === 'Room assigned successfully') {
//         const updatedReservations = reservations.map((reservation) =>
//           reservation.bookingid === bookingId
//             ? { ...reservation, roomid: roomNumber, roomnumber: roomNumber }
//             : reservation
//         );
//         setReservations(updatedReservations);
//         setAllReservations((prev) =>
//           prev.map((r) =>
//             r.bookingid === bookingId ? { ...r, roomid: roomNumber, roomnumber: roomNumber } : r
//           )
//         );
//         setShowAssignRoomModal(false);
//         toast.success('Room assigned successfully!');
//       }
//     } catch (error) {
//       toast.error('Failed to assign room.');
//     }
//   };

//   const handleReassignRoomSubmit = async () => {
//     if (!newRoomNumber.trim()) {
//       toast.error('Please enter a room number');
//       return;
//     }

//     setReassignLoading(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/reassign-room/`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({
//           bookingid: selectedReservation.bookingid,
//           new_room_number: parseInt(newRoomNumber),
//         }),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         const updatedReservations = reservations.map((reservation) =>
//           reservation.bookingid === selectedReservation.bookingid
//             ? { ...reservation, roomnumber: data.new_room_number }
//             : reservation
//         );
//         setReservations(updatedReservations);
//         setAllReservations((prev) =>
//           prev.map((r) =>
//             r.bookingid === selectedReservation.bookingid
//               ? { ...r, roomnumber: data.new_room_number }
//               : r
//           )
//         );
//         toast.success(`Room reassigned to ${data.new_room_number} successfully!`);
//         setShowReassignRoomModal(false);
//         setNewRoomNumber('');
//       } else {
//         if (data.detail) {
//           toast.error(data.detail);
//         } else if (data.message) {
//           toast.error(data.message);
//         } else {
//           toast.error('Failed to reassign room');
//         }
//       }
//     } catch (error) {
//       console.error('Error reassigning room:', error);
//       toast.error('An error occurred while reassigning room');
//     } finally {
//       setReassignLoading(false);
//     }
//   };

//   const handleRowClick = (guestId) => {
//     navigate(`/dashboard/guest/${guestId}`);
//   };

//   const handleCancelBooking = async () => {
//     if (!cancellationReason.trim()) {
//       toast.error('Please provide a cancellation reason');
//       return;
//     }

//     setCancellationLoading(true);
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/modify/${selectedReservation.bookingid}/cancel`,
//         {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             cancellation_reason: cancellationReason,
//           }),
//         }
//       );

//       const data = await response.json();

//       if (!response.ok) {
//         throw new Error(data.message || 'Something went wrong while cancelling the booking.');
//       }

//       setCancellationResponse(data);

//       if (data.status === 'success') {
//         const updatedReservations = reservations.map((reservation) =>
//           reservation.bookingid === selectedReservation.bookingid
//             ? { ...reservation, booking_status: 'Cancelled' }
//             : reservation
//         );
//         setReservations(updatedReservations);
//         setAllReservations((prev) =>
//           prev.map((r) =>
//             r.bookingid === selectedReservation.bookingid
//               ? { ...r, booking_status: 'Cancelled' }
//               : r
//           )
//         );
//         toast.success('Booking cancelled successfully!');
//       }
//     } catch (error) {
//       console.error('Error cancelling booking:', error);
//       toast.error(error.message || 'An unexpected error occurred.');
//       handleCloseModal();
//     } finally {
//       setCancellationLoading(false);
//     }
//   };

//   const resetCancellationFlow = () => {
//     setCancellationReason('');
//     setCancellationResponse(null);
//     setCancellationLoading(false);
//     setShowCancelModal(false);
//   };

//   const formatToTwoDecimal = (amount) => {
//     return parseFloat(amount || 0).toFixed(2);
//   };

//   const handleOpenModal = (reservation) => {
//     setSelectedReservation(reservation);
//     setShowDamageModal(true);
//   };

//   const handleCheckout = async (reservation) => {
//     try {
//       const checkoutResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/check-out?orderid=${reservation.orderid}`,
//         {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//           },
//         }
//       );

//       const checkoutData = await checkoutResponse.json();

//       if (!checkoutResponse.ok) {
//         if (checkoutData.detail) {
//           toast.error(
//             typeof checkoutData.detail === 'string'
//               ? checkoutData.detail
//               : checkoutData.detail?.message || 'Checkout failed'
//           );
//         } else {
//           toast.error('Checkout failed');
//         }
//         return;
//       }

//       if (checkoutData.success) {
//         toast.success(checkoutData.message || 'Checkout successful!');

//         const clearResponse = await fetch(`${BQ_BASE_URL}/bq/api/clear-checkout-status/`, {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             booking_id: reservation.bookingid,
//           }),
//         });

//         const clearData = await clearResponse.json();

//         if (clearData.success) {
//           toast.success(clearData.message || 'Checkout status cleared successfully');
//           const updatedReservations = reservations.map((r) =>
//             r.bookingid === reservation.bookingid
//               ? {
//                   ...r,
//                   is_checkout_cleared: true,
//                   booking_status: 'Checkedout',
//                 }
//               : r
//           );
//           setReservations(updatedReservations);
//           setAllReservations((prev) =>
//             prev.map((r) =>
//               r.bookingid === reservation.bookingid
//                 ? {
//                     ...r,
//                     is_checkout_cleared: true,
//                     booking_status: 'Checkedout',
//                   }
//                 : r
//             )
//           );
//         } else {
//           toast.warning('Checkout completed but status update failed');
//         }

//         setShowCheckoutModal(false);
//       } else {
//         toast.error(checkoutData.message || 'Checkout failed');
//       }
//     } catch (error) {
//       console.error('Error during checkout:', error);
//       toast.error('An error occurred during checkout');
//     }
//   };

//   const handleSendInvoice = async (orderId) => {
//     try {
//       setInvoiceLoading(true);
//       setInvoiceLoadingId(orderId);

//       const reservation = reservations.find((r) => r.orderid === orderId);

//       if (!reservation) {
//         toast.error('Reservation not found');
//         return;
//       }

//       const recipientEmail = reservation.emailid;

//       if (!recipientEmail) {
//         toast.error('Guest email not found');
//         return;
//       }

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/invoice/email/${orderId}?recipient_email=${encodeURIComponent(recipientEmail)}`,
//         {
//           method: 'POST',
//           headers: {
//             Accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//         }
//       );

//       const data = await response.json();

//       if (!response.ok) {
//         throw new Error(data.message || 'Failed to send invoice email');
//       }

//       toast.success(
//         <div>
//           <strong>Invoice Sent!</strong>
//           <br />
//           Invoice has been sent to {recipientEmail}
//         </div>,
//         {
//           position: 'top-center',
//           autoClose: 5000,
//         }
//       );

//       setShowInvoiceModal(false);
//     } catch (error) {
//       console.error('Error sending invoice:', error);
//       toast.error('Failed to send invoice. Please try again.');
//     } finally {
//       setInvoiceLoading(false);
//       setInvoiceLoadingId(null);
//     }
//   };

//   if (isLoading) {
//     return (
//       <div className="flex justify-center items-center py-12">
//         <div className="text-center">
//           <div className="w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
//           <p className="text-gray-500">Loading reservations...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="flex flex-col mt-4">
//       {/* Show date filter info */}
//       {dateFilter !== 'all' && dateRange && dateRange.start && dateRange.end && (
//         <div className="mb-3 px-4 py-2 bg-gray-200 border border-gray-200 rounded-lg text-sm text-gray-800">
//           <span className="font-medium">Showing check-in dates from:</span>{' '}
//           {dateRange.start.toLocaleDateString('en-IN', {
//             day: 'numeric',
//             month: 'short',
//             year: 'numeric',
//           })}
//           {' to '}
//           {dateRange.end.toLocaleDateString('en-IN', {
//             day: 'numeric',
//             month: 'short',
//             year: 'numeric',
//           })}
//           <span className="ml-2 text-xs bg-blue-100 px-2 py-1 rounded-full">
//             {filteredReservations.length} reservations found
//           </span>
//         </div>
//       )}

//       <div className="overflow-x-auto">
//         <table className="table-auto w-full border-collapse border border-gray-300 text-[15px]">
//           <thead>
//             <tr className="bg-gray-200">
//               <th className="border border-gray-300 px-1 py-2 text-center w-12">Sr. No.</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-28">Reservation No.</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-28">Booking Id.</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-36">Guest Name</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-32">Room Type</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-20">Room No.</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-28">Check-In</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-28">Check-Out</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-20">Type</th>
//               <th className="border border-gray-300 py-2 text-center w-16">Total Guests</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-20">Guest</th>
//               <th className="border border-gray-300 px-1 py-2 text-center w-48">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {currentReservations.length > 0 ? (
//               currentReservations.map((reservation, index) => (
//                 <tr
//                   key={index}
//                   className="hover:bg-gray-100 transition cursor-pointer"
//                   onClick={() => handleRowClick(reservation.guestid)}
//                 >
//                   <td className="border border-gray-300 px-1 py-2 text-center">
//                     {index + 1 + indexOfFirstReservation}
//                   </td>
//                   <td className="border border-gray-300 px-2 py-2">{reservation.orderid}</td>
//                   <td className="border border-gray-300 px-2 py-2">{reservation.bookingid}</td>
//                   <td className="border border-gray-300 px-2 py-2 capitalize">
//                     {reservation.firstname} {reservation.lastname}
//                   </td>
//                   <td className="border border-gray-300 px-2 py-2">{reservation.roomtypename}</td>
//                   <td className="border border-gray-300 px-2 py-2">
//                     {reservation.roomnumber ? reservation.roomnumber : 'N/A'}
//                   </td>
//                   <td className="border border-gray-300 px-1 py-2 whitespace-nowrap">
//                     {reservation.checkindate}
//                   </td>
//                   <td className="border border-gray-300 px-1 py-2 whitespace-nowrap">
//                     {reservation.checkoutdate}
//                   </td>
//                   <td className="border border-gray-300 px-1 py-2 text-center">
//                     {reservation.booking_type}
//                   </td>
//                   <td className="border border-gray-300 px-2 py-2">{reservation.numberofguests}</td>
//                   <td className="border border-gray-300 px-4 py-2">
//                     {reservation.clienttype === 'Leisure' || reservation.clienttype === 'leisure'
//                       ? 'Leisure'
//                       : reservation.clienttype === 'Corporate' ||
//                           reservation.clienttype === 'corporate'
//                         ? 'Corporate'
//                         : reservation.clienttype || 'L'}
//                   </td>
//                   <td className="border border-gray-300 px-1 py-1 text-center">
//                     {/* Actions remain the same as your original code */}
//                     {reservation.booking_status === 'Cancelled' ? (
//                       <span className="text-gray-500 text-sm font-semibold">Cancelled</span>
//                     ) : reservation.booking_status === 'Checkedout' ? (
//                       <div className="flex items-center justify-center gap-1">
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             setSelectedReservation(reservation);
//                             handleSendInvoice(reservation.orderid);
//                           }}
//                           disabled={invoiceLoading && invoiceLoadingId === reservation.orderid}
//                           className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors relative group"
//                           title="Send Invoice"
//                         >
//                           {invoiceLoading && invoiceLoadingId === reservation.orderid ? (
//                             <svg
//                               className="animate-spin h-4 w-4 text-white"
//                               xmlns="http://www.w3.org/2000/svg"
//                               fill="none"
//                               viewBox="0 0 24 24"
//                             >
//                               <circle
//                                 className="opacity-25"
//                                 cx="12"
//                                 cy="12"
//                                 r="10"
//                                 stroke="currentColor"
//                                 strokeWidth="4"
//                               ></circle>
//                               <path
//                                 className="opacity-75"
//                                 fill="currentColor"
//                                 d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                               ></path>
//                             </svg>
//                           ) : (
//                             <svg
//                               className="h-4 w-4"
//                               fill="none"
//                               stroke="currentColor"
//                               viewBox="0 0 24 24"
//                             >
//                               <path
//                                 strokeLinecap="round"
//                                 strokeLinejoin="round"
//                                 strokeWidth="2"
//                                 d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
//                               />
//                             </svg>
//                           )}
//                           <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                             Send Invoice
//                           </span>
//                         </button>
//                       </div>
//                     ) : (
//                       <div className="flex items-center justify-center gap-1 flex-wrap">
//                         {/* All your action buttons remain the same */}
//                         {reservation.roomid && reservation.roomnumber ? (
//                           reservation.booking_status === 'Hard' ||
//                           reservation.keyhandover === 'Handed' ? (
//                             <button
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 handleReassignRoom(reservation.bookingid);
//                               }}
//                               className="p-1.5 rounded-lg bg-gray-900 hover:bg-gray-950 text-white transition-colors relative group"
//                               title="Reassign Room"
//                             >
//                               <svg
//                                 className="h-4 w-4"
//                                 fill="none"
//                                 stroke="currentColor"
//                                 viewBox="0 0 24 24"
//                               >
//                                 <path
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                   strokeWidth="2"
//                                   d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
//                                 />
//                               </svg>
//                               <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                                 Reassign Room
//                               </span>
//                             </button>
//                           ) : (
//                             <>
//                               <button
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   handleKeyHandover(reservation.bookingid);
//                                 }}
//                                 className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group"
//                                 title="Key Handover"
//                               >
//                                 <svg
//                                   className="h-4 w-4"
//                                   fill="none"
//                                   stroke="currentColor"
//                                   viewBox="0 0 24 24"
//                                 >
//                                   <path
//                                     strokeLinecap="round"
//                                     strokeLinejoin="round"
//                                     strokeWidth="2"
//                                     d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
//                                   />
//                                 </svg>
//                                 <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                                   Key Handover
//                                 </span>
//                               </button>
//                               <button
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   handleReassignRoom(reservation.bookingid);
//                                 }}
//                                 className="p-1.5 rounded-lg bg-gray-900 hover:bg-gray-950 text-white transition-colors relative group"
//                                 title="Reassign Room"
//                               >
//                                 <svg
//                                   className="h-4 w-4"
//                                   fill="none"
//                                   stroke="currentColor"
//                                   viewBox="0 0 24 24"
//                                 >
//                                   <path
//                                     strokeLinecap="round"
//                                     strokeLinejoin="round"
//                                     strokeWidth="2"
//                                     d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
//                                   />
//                                 </svg>
//                                 <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                                   Reassign Room
//                                 </span>
//                               </button>
//                             </>
//                           )
//                         ) : (
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               handleAssignRoom(reservation.bookingid);
//                             }}
//                             className="p-1.5 rounded-lg bg-gray-900 hover:bg-gray-950 text-white transition-colors relative group"
//                             title="Assign Room"
//                           >
//                             <svg
//                               className="h-4 w-4"
//                               fill="none"
//                               stroke="currentColor"
//                               viewBox="0 0 24 24"
//                             >
//                               <path
//                                 strokeLinecap="round"
//                                 strokeLinejoin="round"
//                                 strokeWidth="2"
//                                 d="M12 6v6m0 0v6m0-6h6m-6 0H6"
//                               />
//                             </svg>
//                             <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                               Assign Room
//                             </span>
//                           </button>
//                         )}

//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             setSelectedReservation(reservation);
//                             setCancellationResponse(null);
//                             setCancellationReason('');
//                             setShowCancelModal(true);
//                           }}
//                           className="p-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors relative group"
//                           title="Cancel Booking"
//                         >
//                           <svg
//                             className="h-4 w-4"
//                             fill="none"
//                             stroke="currentColor"
//                             viewBox="0 0 24 24"
//                           >
//                             <path
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                               strokeWidth="2"
//                               d="M6 18L18 6M6 6l12 12"
//                             />
//                           </svg>
//                           <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                             Cancel Booking
//                           </span>
//                         </button>

//                         <Link
//                           to="/dashboard/core-modules/property-management-system/front-desk-operations/room-upgrade"
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             sessionStorage.setItem(
//                               'selectedReservation',
//                               JSON.stringify(reservation)
//                             );
//                           }}
//                           className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group inline-flex"
//                           title="Upgrade Room"
//                         >
//                           <svg
//                             className="h-4 w-4"
//                             fill="none"
//                             stroke="currentColor"
//                             viewBox="0 0 24 24"
//                           >
//                             <path
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                               strokeWidth="2"
//                               d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
//                             />
//                           </svg>
//                           <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                             Upgrade Room
//                           </span>
//                         </Link>

//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleOpenModal(reservation);
//                           }}
//                           className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group"
//                           title="Report Damage"
//                         >
//                           <FaHammer className="h-4 w-4" />
//                           <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                             Report Damage
//                           </span>
//                         </button>

//                         {reservation.booking_status === 'Checkedout' ||
//                         reservation.is_checkout_cleared ? (
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               setSelectedReservation(reservation);
//                               handleSendInvoice(reservation.orderid);
//                             }}
//                             disabled={invoiceLoading && invoiceLoadingId === reservation.orderid}
//                             className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors relative group"
//                             title="Send Invoice"
//                           >
//                             {invoiceLoading && invoiceLoadingId === reservation.orderid ? (
//                               <svg
//                                 className="animate-spin h-4 w-4 text-white"
//                                 xmlns="http://www.w3.org/2000/svg"
//                                 fill="none"
//                                 viewBox="0 0 24 24"
//                               >
//                                 <circle
//                                   className="opacity-25"
//                                   cx="12"
//                                   cy="12"
//                                   r="10"
//                                   stroke="currentColor"
//                                   strokeWidth="4"
//                                 ></circle>
//                                 <path
//                                   className="opacity-75"
//                                   fill="currentColor"
//                                   d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                                 ></path>
//                               </svg>
//                             ) : (
//                               <svg
//                                 className="h-4 w-4"
//                                 fill="none"
//                                 stroke="currentColor"
//                                 viewBox="0 0 24 24"
//                               >
//                                 <path
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                   strokeWidth="2"
//                                   d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
//                                 />
//                               </svg>
//                             )}
//                             <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                               Send Invoice
//                             </span>
//                           </button>
//                         ) : (
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               setSelectedReservation(reservation);
//                               setShowCheckoutModal(true);
//                             }}
//                             className="p-1.5 rounded-lg bg-black hover:bg-gray-800 text-white transition-colors relative group"
//                             title="Checkout"
//                           >
//                             <svg
//                               className="h-4 w-4"
//                               fill="none"
//                               stroke="currentColor"
//                               viewBox="0 0 24 24"
//                             >
//                               <path
//                                 strokeLinecap="round"
//                                 strokeLinejoin="round"
//                                 strokeWidth="2"
//                                 d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
//                               />
//                             </svg>
//                             <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
//                               Checkout
//                             </span>
//                           </button>
//                         )}
//                       </div>
//                     )}
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="12" className="text-center py-4 text-gray-500">
//                   {dateFilter !== 'all'
//                     ? `No reservations found for the selected date range.`
//                     : 'No reservations found.'}
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination Section */}
//       {filteredReservations.length > 0 && (
//         <div className="flex justify-center mt-8 text-xs gap-2 items-center flex-wrap">
//           <button
//             onClick={handlePrevPage}
//             disabled={currentPage === 1}
//             className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
//           >
//             Previous
//           </button>

//           {currentPage > 10 && (
//             <>
//               <button
//                 onClick={() => handlePageChange(1)}
//                 className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px]"
//               >
//                 1
//               </button>
//               <span className="px-2">...</span>
//             </>
//           )}

//           {getVisiblePages().map((page) => (
//             <button
//               key={page}
//               onClick={() => handlePageChange(page)}
//               className={`px-2 py-2 mx-1 min-w-[35px] ${
//                 currentPage === page
//                   ? 'bg-black text-white rounded-full'
//                   : 'bg-gray-300 text-black rounded-full hover:bg-gray-400'
//               }`}
//             >
//               {page}
//             </button>
//           ))}

//           {currentPage < totalPages - 9 && (
//             <>
//               <span className="px-2">...</span>
//               <button
//                 onClick={() => handlePageChange(totalPages)}
//                 className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px]"
//               >
//                 {totalPages}
//               </button>
//             </>
//           )}

//           <button
//             onClick={handleNextPage}
//             disabled={currentPage === totalPages}
//             className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
//           >
//             Next
//           </button>
//         </div>
//       )}

//       {/* All Modals remain the same as your original code */}
//       {showModal && (
//         <div className="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
//           <div className="bg-white w-full max-w-screen-md max-h-[90vh] overflow-y-auto rounded-lg">
//             <KeyHandover
//               reservation={selectedReservation}
//               onClose={handleCloseModal}
//               onHandoverKey={handleHandoverKey}
//             />
//           </div>
//         </div>
//       )}

//       {showAssignRoomModal && (
//         <div className="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
//           <div className="bg-white w-full max-w-screen-md max-h-[90vh] overflow-y-auto rounded-lg">
//             <AssignRoomModal
//               reservation={selectedReservation}
//               onClose={handleCloseModal}
//               onAssignRoom={handleAssignRoomSubmit}
//             />
//           </div>
//         </div>
//       )}

//       {showReassignRoomModal && selectedReservation && (
//         <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-2xl relative overflow-hidden shadow-2xl w-full max-w-md">
//             <button
//               onClick={handleCloseModal}
//               className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-2 rounded-full transition-colors z-10"
//             >
//               <FiX className="h-5 w-5" />
//             </button>
//             <div className="p-6">
//               <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
//                 <FiRefreshCw className="h-6 w-6 text-blue-600" />
//                 Reassign Room
//               </h3>
//               <div className="mb-6">
//                 <p className="text-sm text-gray-600 mb-4">
//                   Current Room:{' '}
//                   <span className="font-semibold text-gray-900">
//                     {selectedReservation.roomnumber}
//                   </span>
//                 </p>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   New Room Number *
//                 </label>
//                 <input
//                   type="number"
//                   className="w-full px-4 py-3 border border-gray-300 rounded-lg"
//                   placeholder="Enter new room number"
//                   value={newRoomNumber}
//                   onChange={(e) => setNewRoomNumber(e.target.value)}
//                   autoFocus
//                 />
//               </div>
//               <div className="flex justify-end space-x-3">
//                 <button
//                   onClick={handleCloseModal}
//                   className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={handleReassignRoomSubmit}
//                   disabled={!newRoomNumber.trim() || reassignLoading}
//                   className={`px-6 py-3 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${
//                     !newRoomNumber.trim() || reassignLoading
//                       ? 'bg-gray-400 cursor-not-allowed'
//                       : 'bg-gray-900 hover:bg-gray-950'
//                   }`}
//                 >
//                   {reassignLoading ? (
//                     <>
//                       <svg
//                         className="animate-spin h-4 w-4 text-white"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                       >
//                         <circle
//                           className="opacity-25"
//                           cx="12"
//                           cy="12"
//                           r="10"
//                           stroke="currentColor"
//                           strokeWidth="4"
//                         ></circle>
//                         <path
//                           className="opacity-75"
//                           fill="currentColor"
//                           d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                         ></path>
//                       </svg>
//                       Reassigning...
//                     </>
//                   ) : (
//                     <>
//                       <FiRefreshCw className="h-4 w-4" />
//                       Reassign Room
//                     </>
//                   )}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {showCancelModal && selectedReservation && (
//         <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-2xl relative overflow-hidden shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
//             <button
//               onClick={resetCancellationFlow}
//               className="absolute top-4 right-4 text-red-500 hover:text-red-700 p-2 rounded-full transition-colors z-10 bg-white/80"
//             >
//               <FiX className="h-5 w-5" />
//             </button>
//             <div className="overflow-y-auto flex-1">
//               {cancellationResponse ? (
//                 <div className="p-6">
//                   <div className="text-center mb-8">
//                     <div className="mx-auto mb-4">
//                       {cancellationResponse.status === 'success' ? (
//                         <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
//                           <FiCheckCircle className="h-10 w-10 text-green-600" />
//                         </div>
//                       ) : (
//                         <AiOutlineWarning className="mx-auto text-red-600 text-6xl mb-4" />
//                       )}
//                     </div>
//                     <h3 className="text-2xl font-bold text-gray-900 mb-2">
//                       {cancellationResponse.status === 'success'
//                         ? 'Reservation Cancelled Successfully'
//                         : 'Cancellation Failed'}
//                     </h3>
//                     <p className="text-gray-600 mb-6">{cancellationResponse.message}</p>
//                     <div className="inline-flex flex-col items-center px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
//                       <p className="text-xs text-gray-500 font-medium mb-1">RESERVATION NUMBER</p>
//                       <p className="text-lg font-mono font-bold text-gray-900">
//                         {selectedReservation.orderid}
//                       </p>
//                     </div>
//                   </div>

//                   {cancellationResponse.status === 'success' && cancellationResponse.data && (
//                     <div className="space-y-6">
//                       <div className="space-y-4">
//                         <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
//                           Financial Summary
//                         </h4>
//                         <div className="space-y-3">
//                           <div className="flex justify-between items-center">
//                             <div>
//                               <span className="text-gray-700">Cancellation Charges</span>
//                             </div>
//                             <span className="font-semibold text-red-600">
//                               ₹{formatToTwoDecimal(cancellationResponse.data.room_penalty)}
//                             </span>
//                           </div>
//                           <div className="">
//                             <div className="flex justify-between items-center mb-2">
//                               <span className="text-gray-700">Additional Service Refunds</span>
//                               <span className="font-semibold text-gray-950">
//                                 ₹{formatToTwoDecimal(cancellationResponse.data.service_refund)}
//                               </span>
//                             </div>
//                             {cancellationResponse.data.non_refundable_service_retained > 0 && (
//                               <div className="flex justify-between items-center mt-1 text-sm">
//                                 <span className="text-gray-600">
//                                   Non-refundable services retained
//                                 </span>
//                                 <span className="text-red-500">
//                                   ₹
//                                   {formatToTwoDecimal(
//                                     cancellationResponse.data.non_refundable_service_retained
//                                   )}
//                                 </span>
//                               </div>
//                             )}
//                           </div>
//                           <div className="space-y-2 pt-4 border-t border-gray-200">
//                             <div className="flex justify-between items-center">
//                               <span className="text-gray-700 font-medium">Total Refund</span>
//                               <span className="text-xl font-bold text-green-600">
//                                 ₹{formatToTwoDecimal(cancellationResponse.data.total_refund)}
//                               </span>
//                             </div>
//                           </div>
//                         </div>
//                       </div>

//                       {cancellationResponse.data.cancelled_service_items?.length > 0 && (
//                         <div className="space-y-3">
//                           <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
//                             Cancelled Services (
//                             {cancellationResponse.data.cancelled_service_items.length})
//                           </h4>
//                           <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
//                             {cancellationResponse.data.cancelled_service_items.map(
//                               (item, index) => (
//                                 <div
//                                   key={index}
//                                   className={`p-4 rounded-lg border ${
//                                     item.is_refundable
//                                       ? 'bg-green-50 border-green-100'
//                                       : 'bg-gray-50 border-gray-100'
//                                   }`}
//                                 >
//                                   <div className="flex justify-between items-start mb-2">
//                                     <div>
//                                       <span className="font-medium text-gray-900">
//                                         {item.name || item.title}
//                                       </span>
//                                       <div className="flex items-center mt-1">
//                                         <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 mr-2">
//                                           {item.type === 'room_service'
//                                             ? 'Room Service'
//                                             : item.type === 'food'
//                                               ? 'Food'
//                                               : item.type === 'amenity'
//                                                 ? 'Amenity'
//                                                 : item.type}
//                                         </span>
//                                         <span className="text-xs text-gray-500">
//                                           Qty: {item.quantity_cancelled || item.quantity}
//                                         </span>
//                                       </div>
//                                     </div>
//                                     <div className="text-right">
//                                       <span className="font-medium">
//                                         ₹{formatToTwoDecimal(item.total_amount)}
//                                       </span>
//                                       <div
//                                         className={`text-xs mt-1 px-2 py-1 rounded-full ${
//                                           item.is_refundable
//                                             ? 'bg-green-100 text-green-800'
//                                             : 'bg-red-100 text-red-800'
//                                         }`}
//                                       >
//                                         {item.is_refundable ? 'Refundable' : 'Non-refundable'}
//                                       </div>
//                                     </div>
//                                   </div>
//                                   {item.refundable_amount > 0 && item.is_refundable && (
//                                     <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
//                                       <span className="text-sm text-gray-600">
//                                         Refundable amount
//                                       </span>
//                                       <span className="text-sm font-medium text-green-600">
//                                         ₹{formatToTwoDecimal(item.refundable_amount)}
//                                       </span>
//                                     </div>
//                                   )}
//                                 </div>
//                               )
//                             )}
//                           </div>
//                         </div>
//                       )}

//                       <div
//                         className={`p-4 rounded-lg border ${
//                           cancellationResponse.data.total_refund > 0
//                             ? 'bg-green-50 border-green-200'
//                             : 'bg-gray-50 border-gray-200'
//                         }`}
//                       >
//                         <div className="flex justify-between items-center mb-2">
//                           <span className="font-bold text-gray-900 text-lg">
//                             {cancellationResponse.data.total_refund > 0
//                               ? 'Final Refund Amount'
//                               : 'No Refund Applicable'}
//                           </span>
//                           <span
//                             className={`text-2xl font-bold ${
//                               cancellationResponse.data.total_refund > 0
//                                 ? 'text-green-600'
//                                 : 'text-gray-600'
//                             }`}
//                           >
//                             ₹{formatToTwoDecimal(cancellationResponse.data.total_refund)}
//                           </span>
//                         </div>
//                         <p className="text-sm text-gray-700 flex items-start">
//                           <FiInfo className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
//                           {cancellationResponse.data.total_refund > 0
//                             ? 'This amount will be refunded to your original payment method within 5-7 business days. All pending services have been cancelled.'
//                             : 'No refund applicable as per cancellation policy. All pending services have been cancelled.'}
//                         </p>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               ) : (
//                 <div className="w-full">
//                   <div className="px-6 py-6 bg-gradient-to-r from-gray-50 to-gray-50 border-b border-gray-200">
//                     <h3 className="text-2xl font-bold text-gray-900 mb-3">Cancel Reservation</h3>
//                     <div className="flex items-center justify-between">
//                       <div>
//                         <p className="text-sm text-gray-600">
//                           Reservation Number: {selectedReservation.orderid}
//                         </p>
//                       </div>
//                       <div className="bg-gray-100 px-3 py-1 rounded-full">
//                         <span className="text-xs font-medium text-gray-700">
//                           {selectedReservation.firstname} {selectedReservation.lastname}
//                         </span>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="px-6 py-4">
//                     <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
//                       <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
//                         <AiOutlineWarning className="h-5 w-5 text-yellow-600 mr-2" />
//                         Cancellation Rules
//                       </h4>
//                       <ul className="space-y-2 text-sm text-gray-700">
//                         <li className="flex items-start">
//                           <span className="text-yellow-600 mr-2">•</span>
//                           Cancellation charges may apply as per the hotel policy
//                         </li>
//                         <li className="flex items-start">
//                           <span className="text-yellow-600 mr-2">•</span>
//                           All pending services (amenities, food orders, room services) will be
//                           automatically cancelled
//                         </li>
//                         <li className="flex items-start">
//                           <span className="text-yellow-600 mr-2">•</span>
//                           Refundable amounts will be processed within 5-7 business days
//                         </li>
//                         <li className="flex items-start">
//                           <span className="text-yellow-600 mr-2">•</span>
//                           Non-refundable services will be retained and charged
//                         </li>
//                         <li className="flex items-start">
//                           <span className="text-yellow-600 mr-2">•</span>A detailed financial
//                           breakdown will be shown after cancellation
//                         </li>
//                       </ul>
//                     </div>

//                     <div className="space-y-4">
//                       <div>
//                         <label className="block text-sm font-semibold text-gray-700 mb-2">
//                           Reason for Cancellation *
//                         </label>
//                         <textarea
//                           rows={4}
//                           className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all placeholder:text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
//                           placeholder="Please share your reason for cancelling this reservation..."
//                           value={cancellationReason}
//                           onChange={(e) => setCancellationReason(e.target.value)}
//                           required
//                         />
//                         <p className="text-xs text-gray-500 mt-1">
//                           Your feedback helps us improve our services.
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>

//             {!cancellationResponse ? (
//               <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end space-x-3">
//                 <button
//                   onClick={resetCancellationFlow}
//                   className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
//                 >
//                   Go Back
//                 </button>
//                 <button
//                   onClick={handleCancelBooking}
//                   disabled={!cancellationReason.trim() || cancellationLoading}
//                   className={`px-8 py-3 rounded-lg font-medium text-white transition-colors ${
//                     !cancellationReason.trim() || cancellationLoading
//                       ? 'bg-gray-400 cursor-not-allowed'
//                       : 'bg-red-600 hover:bg-red-700 shadow-lg'
//                   }`}
//                 >
//                   {cancellationLoading ? (
//                     <span className="flex items-center justify-center">
//                       <svg
//                         className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                       >
//                         <circle
//                           className="opacity-25"
//                           cx="12"
//                           cy="12"
//                           r="10"
//                           stroke="currentColor"
//                           strokeWidth="4"
//                         ></circle>
//                         <path
//                           className="opacity-75"
//                           fill="currentColor"
//                           d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                         ></path>
//                       </svg>
//                       Processing...
//                     </span>
//                   ) : (
//                     'Confirm Cancellation'
//                   )}
//                 </button>
//               </div>
//             ) : (
//               <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end">
//                 <button
//                   onClick={resetCancellationFlow}
//                   className="px-8 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors shadow-lg"
//                 >
//                   Close
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>
//       )}

//       {showDamageModal && selectedReservation && (
//         <DamageReportModal
//           isOpen={showDamageModal}
//           onClose={() => setShowDamageModal(false)}
//           reservation={selectedReservation}
//         />
//       )}

//       {showCheckoutModal && selectedReservation && (
//         <CheckoutConfirmationModal
//           reservation={selectedReservation}
//           onClose={() => setShowCheckoutModal(false)}
//           onConfirm={() => handleCheckout(selectedReservation)}
//         />
//       )}

//       {showInvoiceModal && selectedReservation && (
//         <InvoiceModal reservation={selectedReservation} onClose={handleCloseModal} />
//       )}
//     </div>
//   );
// };

// export default ReservationTable;
